import React, { useEffect, useRef } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";

/* ─── Global fonts ───────────────────────────────────────────────── */
const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');

  :root {
    --navy:        #080D1A;
    --navy-card:   #0F1624;
    --navy-raised: #131B2E;
    --gold:        #C9A84C;
    --gold-light:  #E2C47A;
    --gold-dim:    rgba(201,168,76,0.10);
    --gold-border: rgba(201,168,76,0.22);
    --text-primary:   #F0E9D6;
    --text-secondary: #A8B4C8;
    --text-muted:     #5A6880;
    --font-display: 'Cormorant Garamond', Georgia, serif;
    --font-body:    'Outfit', sans-serif;
  }
`;

/* ─── Keyframes ──────────────────────────────────────────────────── */
const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-8px); }
`;

/* ─── Section ────────────────────────────────────────────────────── */
const Section = styled.section`
  background: var(--navy);
  padding: 8rem 0 6rem;
  position: relative;
  overflow: hidden;
  font-family: var(--font-body);

  &::before {
    content: "";
    position: absolute;
    width: 650px; height: 650px;
    top: -220px; right: -180px;
    background: radial-gradient(circle, rgba(201,168,76,0.065) 0%, transparent 70%);
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    width: 550px; height: 550px;
    bottom: -200px; left: -130px;
    background: radial-gradient(circle, rgba(30,60,120,0.16) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) { padding: 5rem 0 4rem; }
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(201,168,76,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(201,168,76,0.025) 1px, transparent 1px);
  background-size: 58px 58px;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 2.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) { padding: 0 1.25rem; }
`;

/* ─── Shared reveal state ────────────────────────────────────────── */
const revealStyles = `
  opacity: 0;
  transform: translateY(26px);
  transition: opacity 0.72s ease, transform 0.72s ease;
  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* ─── Top grid ───────────────────────────────────────────────────── */
const TopGrid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 5rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3.5rem;
  }
`;

/* ─── Text column ────────────────────────────────────────────────── */
const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--gold);
  border: 1px solid var(--gold-border);
  background: var(--gold-dim);
  padding: 0.55rem 1.1rem;
  border-radius: 2px;
  margin-bottom: 1.8rem;

  &::before {
    content: "";
    width: 18px; height: 1px;
    background: var(--gold);
    display: inline-block;
    flex-shrink: 0;
  }

  ${revealStyles}
  transition-delay: 0.05s;
`;

const Heading = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(2.8rem, 4.5vw, 4.4rem);
  font-weight: 400;
  line-height: 1.06;
  letter-spacing: -0.3px;
  color: var(--text-primary);
  margin-bottom: 1.8rem;

  em {
    font-style: italic;
    font-weight: 300;
    color: var(--gold-light);
  }

  ${revealStyles}
  transition-delay: 0.15s;
`;

const Rule = styled.div`
  height: 1px;
  width: 72px;
  background: linear-gradient(90deg, var(--gold), transparent);
  margin-bottom: 1.8rem;
  transform-origin: left;

  opacity: 0;
  transform: scaleX(0);
  transition: opacity 0.7s ease 0.28s, transform 0.7s ease 0.28s;

  &.is-visible {
    opacity: 1;
    transform: scaleX(1);
  }
`;

const BodyText = styled.p`
  font-size: 0.96rem;
  font-weight: 300;
  line-height: 1.95;
  color: var(--text-secondary);
  max-width: 570px;
  margin-bottom: 1.3rem;

  ${revealStyles}

  &.delay-1 { transition-delay: 0.32s; }
  &.delay-2 { transition-delay: 0.42s; }
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 2.4rem;
`;

const Badge = styled.div`
  background: var(--navy-raised);
  border: 1px solid var(--gold-border);
  border-radius: 3px;
  padding: 0.9rem 1.25rem;
  min-width: 165px;
  cursor: default;
  transition: border-color 0.3s ease, background 0.3s ease,
              opacity 0.65s ease, transform 0.65s ease;

  opacity: 0;
  transform: translateY(16px);

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &.delay-1 { transition-delay: 0.55s; }
  &.delay-2 { transition-delay: 0.65s; }
  &.delay-3 { transition-delay: 0.75s; }

  &:hover {
    border-color: var(--gold);
    background: rgba(201, 168, 76, 0.06);
  }

  h5 {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--gold-light);
    margin-bottom: 0.28rem;
    font-family: var(--font-body);
  }
  p {
    font-size: 0.8rem;
    color: var(--text-muted);
    margin: 0;
    line-height: 1.5;
    font-weight: 300;
    font-family: var(--font-body);
  }
`;

/* ─── Stat cards ─────────────────────────────────────────────────── */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.1rem;

  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const StatCard = styled.div`
  background: var(--navy-card);
  border: 1px solid rgba(201, 168, 76, 0.12);
  border-radius: 4px;
  padding: 1.75rem 1.5rem;
  position: relative;
  overflow: hidden;
  cursor: default;

  transition: border-color 0.35s ease, transform 0.35s ease,
              opacity 0.65s ease;

  opacity: 0;
  transform: translateY(22px);

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &.delay-1 { transition-delay: 0.10s; }
  &.delay-2 { transition-delay: 0.20s; }
  &.delay-3 { transition-delay: 0.30s; }
  &.delay-4 { transition-delay: 0.40s; }

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, var(--gold), transparent);
    opacity: 0;
    transition: opacity 0.35s ease;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 30%, rgba(201,168,76,0.04), transparent 65%);
    pointer-events: none;
  }

  &:hover {
    border-color: rgba(201, 168, 76, 0.32);
    transform: translateY(-5px);
  }
  &:hover::before { opacity: 1; }
`;

const StatIconBox = styled.div`
  width: 44px; height: 44px;
  background: var(--gold-dim);
  border: 1px solid var(--gold-border);
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: var(--gold);
  margin-bottom: 1.25rem;
`;

const StatNum = styled.div`
  font-family: var(--font-display);
  font-size: clamp(2.3rem, 3.5vw, 3.2rem);
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1;
  letter-spacing: -1px;
  margin-bottom: 0.45rem;

  span { color: var(--gold); font-weight: 300; }
`;

const StatTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  font-family: var(--font-body);
`;

const StatDesc = styled.p`
  font-size: 0.8rem;
  font-weight: 300;
  color: var(--text-muted);
  line-height: 1.65;
  margin: 0;
  font-family: var(--font-body);
`;

/* ─── Divider ────────────────────────────────────────────────────── */
const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  margin: 6rem 0 5rem;

  opacity: 0;
  transition: opacity 0.8s ease;
  &.is-visible { opacity: 1; }

  &::before, &::after {
    content: "";
    flex: 1; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(201,168,76,0.28), transparent);
  }
`;

const DividerGlyph = styled.div`
  width: 30px; height: 30px;
  border: 1px solid var(--gold-border);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gold);
  font-size: 0.65rem;
  flex-shrink: 0;
  background: var(--navy-raised);
  animation: ${floatY} 4s ease-in-out infinite;
`;

/* ─── Feature cards ──────────────────────────────────────────────── */
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.4rem;

  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.div`
  background: var(--navy-raised);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 2rem 1.75rem;
  position: relative;
  overflow: hidden;
  cursor: default;

  transition: border-color 0.4s ease, opacity 0.65s ease, transform 0.65s ease;

  opacity: 0;
  transform: translateY(22px);

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &.delay-1 { transition-delay: 0.10s; }
  &.delay-2 { transition-delay: 0.22s; }
  &.delay-3 { transition-delay: 0.34s; }

  &::before {
    content: "";
    position: absolute;
    left: 0; top: 18%; bottom: 18%;
    width: 2px;
    background: linear-gradient(to bottom, transparent, var(--gold), transparent);
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    border-color: rgba(201, 168, 76, 0.2);
  }
  &:hover::before { opacity: 1; }
`;

const FeatureIconWrap = styled.div`
  width: 48px; height: 48px;
  background: var(--gold-dim);
  border: 1px solid var(--gold-border);
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: var(--gold);
  margin-bottom: 1.5rem;
  transition: background 0.35s ease, transform 0.35s ease;

  ${FeatureCard}:hover & {
    background: rgba(201, 168, 76, 0.16);
    transform: scale(1.06);
  }
`;

const FeatureTitle = styled.h4`
  font-family: var(--font-display);
  font-size: 1.45rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.8rem;
  line-height: 1.2;
`;

const FeatureDesc = styled.p`
  font-size: 0.88rem;
  font-weight: 300;
  color: var(--text-secondary);
  line-height: 1.85;
  margin: 0;
  font-family: var(--font-body);
`;

/* ─── Data ───────────────────────────────────────────────────────── */
const STATS = [
  {
    icon: "fas fa-briefcase",
    num: "15", suffix: "+",
    label: "Years of Excellence",
    desc: "Deep expertise across corporate, legal, and compliance domains.",
    delay: "delay-1",
  },
  {
    icon: "fas fa-chart-line",
    num: "1K", suffix: "+",
    label: "Cases Resolved",
    desc: "Complex mandates handled with precision and strategic clarity.",
    delay: "delay-2",
  },
  {
    icon: "fas fa-users",
    num: "50", suffix: "+",
    label: "Expert Lawyers",
    desc: "Multidisciplinary professionals across every practice area.",
    delay: "delay-3",
  },
  {
    icon: "fas fa-trophy",
    num: "98", suffix: "%",
    label: "Success Rate",
    desc: "Consistent track record of favourable outcomes for clients.",
    delay: "delay-4",
  },
];

const FEATURES = [
  {
    icon: "fas fa-user-tie",
    title: "Client-Centric Approach",
    desc: "Every engagement begins with listening. We tailor legal strategies to your objectives — not off-the-shelf templates.",
    delay: "delay-1",
  },
  {
    icon: "fas fa-medal",
    title: "Proven Track Record",
    desc: "Over 1,000 concluded matters across litigation, compliance, and advisory mandates spanning two decades.",
    delay: "delay-2",
  },
  {
    icon: "fas fa-bolt",
    title: "Timely Resolution",
    desc: "Structured workflows and senior oversight ensure rapid, high-quality outcomes without compromising on rigour.",
    delay: "delay-3",
  },
];

const BADGES = [
  { title: "Trusted Expertise",  sub: "15+ years of legal excellence",       delay: "delay-1" },
  { title: "Business Focused",   sub: "Corporate & compliance specialists",  delay: "delay-2" },
  { title: "Client Commitment",  sub: "Personalised legal strategies",       delay: "delay-3" },
];

/* ─── Intersection Observer hook ────────────────────────────────── */
function useRevealOnScroll(containerRef) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const targets = container.querySelectorAll("[data-reveal]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [containerRef]);
}

/* ─── Component ──────────────────────────────────────────────────── */
const AboutEnhanced = () => {
  const rootRef = useRef(null);
  useRevealOnScroll(rootRef);

  return (
    <>
      <GlobalStyles />
      <Section id="about" ref={rootRef}>
        <GridOverlay />
        <Container>

          {/* ── Top grid ── */}
          <TopGrid>

            {/* Left: text */}
            <div>
              <Tag data-reveal>About Mundra Exim Consultants</Tag>

              <Heading data-reveal>
                Strategic Legal Excellence<br />
                for <em>Modern Businesses</em>
              </Heading>

              <Rule data-reveal />

              <BodyText data-reveal className="delay-1">
                Mundra Exim Consultants is a premier legal and compliance firm
                delivering high-level advisory solutions across diverse practice
                areas. Backed by over 15 years of professional expertise, we
                provide strategic guidance, precision-driven execution, and
                result-oriented representation for businesses and individuals.
              </BodyText>

              <BodyText data-reveal className="delay-2">
                Our practice is rooted in professionalism, trust, and long-term
                partnerships — delivering tailored legal strategies that align
                with our clients' commercial objectives while maintaining the
                highest standards of integrity.
              </BodyText>

              <Badges>
                {BADGES.map((b) => (
                  <Badge key={b.title} data-reveal className={b.delay}>
                    <h5>{b.title}</h5>
                    <p>{b.sub}</p>
                  </Badge>
                ))}
              </Badges>
            </div>

            {/* Right: stat cards */}
            <StatsGrid>
              {STATS.map((s) => (
                <StatCard key={s.label} data-reveal className={s.delay}>
                  <StatIconBox>
                    <i className={s.icon} aria-hidden="true" />
                  </StatIconBox>
                  <StatNum>
                    {s.num}<span>{s.suffix}</span>
                  </StatNum>
                  <StatTitle>{s.label}</StatTitle>
                  <StatDesc>{s.desc}</StatDesc>
                </StatCard>
              ))}
            </StatsGrid>

          </TopGrid>

          {/* ── Divider ── */}
          <Divider data-reveal>
            <DividerGlyph aria-hidden="true">✦</DividerGlyph>
          </Divider>

          {/* ── Feature cards ── */}
          <FeaturesGrid>
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} data-reveal className={f.delay}>
                <FeatureIconWrap>
                  <i className={f.icon} aria-hidden="true" />
                </FeatureIconWrap>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureCard>
            ))}
          </FeaturesGrid>

        </Container>
      </Section>
    </>
  );
};

export default AboutEnhanced;